import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { submitChatLead } from '../services/chatLeadService';
import { trackInquiry } from '../services/analyticsService';
import HoneypotField, { withHoneypotValue } from './HoneypotField';
import './ConsultationModal.css';

const projectTypes = [
  'Kitchen remodeling',
  'Bathroom remodeling',
  'Flooring',
  'Whole-home remodeling',
  'Countertops',
  'Other renovation'
];

const createInitialForm = projectType => ({
  name: '',
  email: '',
  phoneNumber: '',
  postalCode: '',
  serviceReq: projectTypes.includes(projectType) ? projectType : projectTypes[0],
  message: ''
});

export default function ConsultationModal({ isOpen, initialProject, onClose }) {
  const [formData, setFormData] = useState(() => createInitialForm(initialProject));
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    setFormData(createInitialForm(initialProject));
    setStatus('');
    setSubmitting(false);
    previouslyFocusedRef.current = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector('[data-autofocus]')?.focus();
    }, 40);

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [initialProject, isOpen, onClose]);

  if (!isOpen) return null;

  const update = event => setFormData(current => ({ ...current, [event.target.name]: event.target.value }));

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitting(true);
    setStatus('');

    const serviceReq = `${formData.serviceReq}${formData.message ? ` — ${formData.message}` : ''}`;
    const values = withHoneypotValue({
      name: formData.name,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      postalCode: formData.postalCode,
      serviceReq,
      siteId: SITE_CONFIG.SITE_ID
    }, event.currentTarget);

    try {
      const response = await submitChatLead(values);
      if (!response.ok) throw new Error('Submission failed');
      trackInquiry('consultation_modal', response);
      setStatus('success');
    } catch (error) {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    setFormData(createInitialForm(initialProject));
    setStatus('');
    window.setTimeout(() => dialogRef.current?.querySelector('[data-autofocus]')?.focus(), 0);
  };

  return createPortal(
    <div className="consultation-modal-overlay" onMouseDown={event => event.target === event.currentTarget && onClose()} role="presentation">
      <section ref={dialogRef} className="consultation-modal" role="dialog" aria-modal="true" aria-labelledby="consultation-modal-title" aria-describedby="consultation-modal-description">
        <button className="consultation-modal-close" type="button" onClick={onClose} aria-label="Close consultation form">
          <span aria-hidden="true">×</span>
        </button>

        <aside className="consultation-modal-intro">
          <div>
            <p className="consultation-modal-kicker">Complimentary project consultation</p>
            <h2 id="consultation-modal-title">Let’s talk about your space.</h2>
            <p id="consultation-modal-description">Tell us what you want to change. Our local team will follow up to understand the project and recommend a practical next step.</p>
          </div>
          <ul className="consultation-modal-benefits">
            <li><span aria-hidden="true">01</span><div><strong>Local guidance</strong><small>Talk with a Maricopa renovation professional.</small></div></li>
            <li><span aria-hidden="true">02</span><div><strong>No-pressure conversation</strong><small>Get clarity on scope, priorities and possibilities.</small></div></li>
            <li><span aria-hidden="true">03</span><div><strong>A clear next step</strong><small>Know what makes sense before making decisions.</small></div></li>
          </ul>
          <a className="consultation-modal-phone" href={`tel:${SITE_CONFIG.BUSINESS.PHONE_E164}`}>
            <span>Prefer to call?</span>
            <strong>{SITE_CONFIG.BUSINESS.PHONE}</strong>
          </a>
        </aside>

        <div className="consultation-modal-form-panel">
          {status === 'success' ? (
            <div className="consultation-modal-success" role="status">
              <span className="consultation-success-mark" aria-hidden="true">✓</span>
              <p className="consultation-modal-kicker">Request received</p>
              <h3>Thank you, {formData.name.split(' ')[0]}.</h3>
              <p>Your consultation request is with the Maricopa Renovations team. We’ll be in touch soon to learn more about your project.</p>
              <div className="consultation-success-actions">
                <button className="button" type="button" onClick={onClose}>Done</button>
                <button className="consultation-text-button" type="button" onClick={startAnother}>Send another request</button>
              </div>
            </div>
          ) : (
            <>
              <div className="consultation-form-heading">
                <p className="consultation-modal-kicker">Project details</p>
                <h3>Start your consultation</h3>
                <p>Fields marked with an asterisk are required.</p>
              </div>
              <form className="consultation-form" onSubmit={handleSubmit}>
                <HoneypotField />
                <div className="consultation-field">
                  <label htmlFor="consultation-name">Name <span>*</span></label>
                  <input data-autofocus id="consultation-name" name="name" value={formData.name} onChange={update} autoComplete="name" placeholder="Your full name" required />
                </div>
                <div className="consultation-field">
                  <label htmlFor="consultation-phone">Phone <span>*</span></label>
                  <input id="consultation-phone" name="phoneNumber" type="tel" value={formData.phoneNumber} onChange={update} autoComplete="tel" placeholder="(520) 555-0123" required />
                </div>
                <div className="consultation-field">
                  <label htmlFor="consultation-email">Email <span>*</span></label>
                  <input id="consultation-email" name="email" type="email" value={formData.email} onChange={update} autoComplete="email" placeholder="you@example.com" required />
                </div>
                <div className="consultation-field">
                  <label htmlFor="consultation-zip">ZIP code <span>*</span></label>
                  <input id="consultation-zip" name="postalCode" inputMode="numeric" pattern="[0-9]{5}(-[0-9]{4})?" value={formData.postalCode} onChange={update} autoComplete="postal-code" placeholder="85138" required />
                </div>
                <div className="consultation-field consultation-field-full">
                  <label htmlFor="consultation-project">What are you planning? <span>*</span></label>
                  <select id="consultation-project" name="serviceReq" value={formData.serviceReq} onChange={update} required>
                    {projectTypes.map(project => <option key={project}>{project}</option>)}
                  </select>
                </div>
                <div className="consultation-field consultation-field-full">
                  <label htmlFor="consultation-message">A few details <small>Optional</small></label>
                  <textarea id="consultation-message" name="message" value={formData.message} onChange={update} placeholder="Which room, what would you like to change, and when are you hoping to begin?" rows="3" />
                </div>
                {status === 'error' && <div className="consultation-form-error consultation-field-full" role="alert">We couldn’t send your request. Please try again or call <a href={`tel:${SITE_CONFIG.BUSINESS.PHONE_E164}`}>{SITE_CONFIG.BUSINESS.PHONE}</a>.</div>}
                <div className="consultation-form-footer consultation-field-full">
                  <button className="button consultation-submit" type="submit" disabled={submitting}>
                    {submitting ? <><span className="consultation-spinner" aria-hidden="true" /> Sending request…</> : <>Request my free consultation <span aria-hidden="true">→</span></>}
                  </button>
                  <p>Your details stay private and are only used to respond to your request. See our <Link to="/privacy-policy" onClick={onClose}>privacy policy</Link>.</p>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </div>,
    document.body
  );
}
